import { httpRequest } from './network';

import CryptoJS from 'crypto-js';
import { IOptions, IQuestion } from "../interfaces/Question";
import { STORE } from '../store';
import { newMessageSound } from './notification-handler';
import { updateReplyMessage } from '../actions';
import { updateEnvironment } from '../actions/environment.action';
import axios from 'axios';
import { stopVoiceOutput } from './shared';
axios.defaults.baseURL = process.env.REACT_APP_API_URL;

let currentAudio: HTMLAudioElement | null = null;
let currentSource: any = null;
let currentAudioContext: any = null;

export const queryAIHandler = (data: any) => new Promise((resolve, reject) => {
  const { environment }: any = STORE.getState();

  httpRequest('POST', `artificial-intelligence/ai-response`, { binary: encryptPayload({ ...data, uuid: environment.uuid || '' }) })
    .then((resp: any) => {
      resolve(resp.data);
    })
    .catch(err => { reject(err) })

});

export const stopAudio = () => {
  if (currentAudio) {
    // Stop the audio
    currentAudio.pause();
    currentAudio.currentTime = 0;

    // If we have the audio URL, clean it up
    if (currentAudio.src && currentAudio.src.startsWith('blob:')) {
      URL.revokeObjectURL(currentAudio.src);
    }

    // Clear reference
    currentAudio = null;
    console.log("Audio playback stopped");
  }

  // Stop the Web Audio API source if it exists
  if (currentSource) {
    try {
      currentSource.stop();
    } catch (e) {
      // Source might already be stopped
    }
    currentSource = null;
  }

  if (currentAudioContext && currentAudioContext.state !== 'closed') {
    currentAudioContext.close().catch((e: any) => console.error("Error closing AudioContext:", e));
    currentAudioContext = null;
  }
};

export const generateAudio = (text: any) => new Promise(async (resolve, reject) => {
  try {
    const { environment }: any = STORE.getState();

    stopAudio();

    const response: any = await axios({
      url: 'artificial-intelligence/audio/speak',
      method: 'POST',
      responseType: 'arraybuffer',
      data: { binary: encryptPayload({ text: text.gptAnswer, uuid: environment.uuid || '' }) }
    });

    // For basic audio playback
    const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    currentAudio = audio;

    // Web Audio API with effects
    const audioCtx = new AudioContext();
    currentAudioContext = audioCtx;

    // Decode the ArrayBuffer to AudioBuffer before setting it
    audioCtx.decodeAudioData(response.data, (decodedData) => {
      const source = audioCtx.createBufferSource();
      currentSource = source;
      source.buffer = decodedData; // Now we're setting a proper AudioBuffer

      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 1;

      // === ROBOTIC EFFECT PARAMETERS ===
      // 1. Frequency modulation - adds slight mechanical wavering
      const oscillator = audioCtx.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.value = 7; // 8Hz modulation - adjust 5-15Hz for different robot effects

      const oscillatorGain = audioCtx.createGain();
      oscillatorGain.gain.value = 0.15; // Modulation depth - 0.1-0.3 for subtle to strong effect

      oscillator.connect(oscillatorGain);
      oscillator.start();

      // 2. Ring modulator - creates metallic robot sound
      const ringModulator = audioCtx.createGain();
      const ringOsc = audioCtx.createOscillator();
      ringOsc.type = 'square';
      ringOsc.frequency.value = 60; // Lower for deeper robot, higher for more electronic

      // Create a gain node to control the depth of the ring modulation
      const ringDepth = audioCtx.createGain();
      ringDepth.gain.value = 0.25; // Much more subtle effect (effectively mixing dry/wet)
      ringOsc.connect(ringDepth);
      ringDepth.connect(ringModulator.gain);
      ringOsc.start();

      // 3. Bandpass filter - creates vocoder-like effect
      const bandpass = audioCtx.createBiquadFilter();
      bandpass.type = "bandpass";
      bandpass.frequency.value = 1800; // 1800Hz is good for robotic voice clarity
      bandpass.Q.value = 3; // Narrower bandwidth sounds more synthetic (1-10 range)

      // 4. Second bandpass for formant preservation (human speech characteristic)
      const formantFilter = audioCtx.createBiquadFilter();
      formantFilter.type = "bandpass";
      formantFilter.frequency.value = 550; // Lower frequencies for warmth
      formantFilter.Q.value = 2.5;

      // Compressor for dynamic range - makes quieter parts more audible like human speech
      const compressor = audioCtx.createDynamicsCompressor();
      compressor.threshold.value = -24;   // Start compressing at this level
      compressor.knee.value = 4;         // Gradual compression curve
      compressor.ratio.value = 7;         // Compression ratio
      compressor.attack.value = 0.003;    // Fast attack for natural consonants
      compressor.release.value = 0.25;

      // Optional mild EQ for clarity
      const highShelfFilter = audioCtx.createBiquadFilter();
      highShelfFilter.type = "highshelf";
      highShelfFilter.frequency.value = 6000;
      highShelfFilter.gain.value = 3; // Reduce high frequencies slightly

      const lowShelfFilter = audioCtx.createBiquadFilter();
      lowShelfFilter.type = "lowshelf";
      lowShelfFilter.frequency.value = 400;
      lowShelfFilter.gain.value = 2;

      // 7. Add chorus effect for slight mechanical doubling
      const chorus = audioCtx.createDelay();
      chorus.delayTime.value = 0.04;
      
      const chorusLFO = audioCtx.createOscillator();
      chorusLFO.type = 'sine';
      chorusLFO.frequency.value = 0.2; // Very slow modulation
      
      const chorusDepth = audioCtx.createGain();
      chorusDepth.gain.value = 0.004; // Subtle depth
      
      chorusLFO.connect(chorusDepth);
      chorusDepth.connect(chorus.delayTime);
      chorusLFO.start();

      // 8. Dry/wet mixer for more natural sound
      const dryGain = audioCtx.createGain();
      dryGain.gain.value = 0.3; // 40% dry signal
      
      const wetGain = audioCtx.createGain();
      wetGain.gain.value = 0.5; // 60% wet (processed) signal

      source.connect(dryGain);
      dryGain.connect(gainNode);

      // Main signal flow for processed (wet) signal
      source.connect(lowShelfFilter);
      lowShelfFilter.connect(bandpass);
      bandpass.connect(formantFilter);
      formantFilter.connect(highShelfFilter);
      highShelfFilter.connect(ringModulator);
      ringModulator.connect(chorus);
      chorus.connect(compressor);
      compressor.connect(wetGain);
      wetGain.connect(gainNode);

      // Add subtle frequency modulation to the bandpass
      oscillatorGain.connect(bandpass.frequency);

      // Final output
      gainNode.connect(audioCtx.destination);

      source.start();
      resolve(true);

      source.onended = () => {
        console.log('Audio source playback ended');
        stopVoiceOutput();
      };
    }, (err) => {
      console.error("Error decoding audio data", err);
      // Fallback to simple audio playback if decoding fails
      audio.play().then(() => {
        resolve(true);
      }).catch(err => {
        console.error("Audio play promise rejected:", err);
        resolve(true);
      });
    });

  } catch (error) {
    console.error("Error generating audio:", error);
    reject(error);
    return;
  }
});

export const encryptPayload = (payload: any, secret = 'user-payload') => {
  const newPayload = JSON.stringify(payload);

  return CryptoJS
    .AES
    .encrypt(newPayload, secret)
    .toString();
};

export const decryptPayload = (payload: string, key = 'botpenguin'): any => {
  return JSON.parse(CryptoJS
    .AES
    .decrypt(payload, key)
    .toString(CryptoJS.enc.Utf8));
};

export const saveMessage = async (payload: any) => {
  try {
    const { environment, configuration, messages, flows }: any = STORE.getState();
    let isUserInteracted = environment.isUserInteracted ?? false;
    let botMessageCount = 0;

    if (!isUserInteracted && (messages || []).length) {
      isUserInteracted = messages.some((item: any) => item.position === 'right');
      STORE.dispatch(
        updateEnvironment({ isUserInteracted })
      );

      if (isUserInteracted) {
        botMessageCount = messages.filter((item: any) => item.position !== 'right').length;
      }
    }

    // if user is not present in local storage set it
    if (!localStorage.getItem('__BotPenguinUser')) {
      localStorage.setItem('__BotPenguinUser', environment._user);
      localStorage.setItem('__BotPenguin', environment._id);
    }

    await httpRequest('POST', 'save-response', {
      binary: encryptPayload({
        payload,
        _bot: environment._id,
        _user: environment._user,
        uuid: environment.uuid,
        profile: environment.profile,
        _flow: flows[0]._id,
        isLiveChat: !!localStorage.getItem('agentId'),
        agentId: localStorage.getItem('agentId') || environment._user,
        isUserInteracted,
        ...(botMessageCount && { botMessageCount })
      })
    });
    if (payload.messagedBy === 'bot') {
      newMessageSound(configuration.generalSettings.notificationSound);
    }

    if (Object.keys(payload?.replyInfo || {}).length) {
      STORE.dispatch(updateReplyMessage(null));
    }
    return true;
  } catch (error: any) {
    console.log(JSON.stringify('Error saving response', error))
  }
}

export const updateUUID = async (payload: any) => {
  try {
    const { environment, meta }: any = STORE.getState();

    await httpRequest('PUT', 'update-meta', {
      binary: encryptPayload({
        payload: {
          ...meta,
          ...payload
        },
        _bot: environment._id,
        _user: environment._user,
        uuid: environment.uuid,
        profile: environment.profile
      })
    });
  } catch (error) {
    console.log('Error in updating UuId', error);
  }
};

export const addInstallation = async () => {
  try {
    const { environment }: any = STORE.getState();

    const installations = environment.installations || [];
    let baseUrl = window.location.origin;
    if (!installations.find((data: any) => data.siteUrl === baseUrl)) {
      await httpRequest('POST', 'bot-installation', {
        binary: encryptPayload({
          botId: localStorage.getItem('__BotPenguin'),
          _user: localStorage.getItem('__BotPenguinUser'),
          siteUrl: baseUrl
        })
      });
    }
  }
  catch (error) {
    console.log(error, "error in Adding Installations");
  }
};

export const actionBasedOnChatResponse = async (activeQuestion: IQuestion | any, text: string, _userWebsite: string, _customer?: string) => {
  try {
    if (activeQuestion?.type === 'button') {
      const selectedOption = activeQuestion?.options?.find((option: IOptions) => option.value === text);
      const payload = encryptPayload({
        tagIds: selectedOption?.actionBasedChatResponse?.tagIds ?? [],
        status: selectedOption?.actionBasedChatResponse?.status ?? 'OPEN',
        agentId: selectedOption?.actionBasedChatResponse?.agentId ?? undefined,
        isSubscriber: selectedOption?.actionBasedChatResponse?.isSubscriber ?? true,
        searchQuery: { _userWebsite, _customer }
      })
      await httpRequest('PUT', `chat-response-actions`, { binary: payload });
    }
    return true;
  } catch (err) {
    throw new Error(JSON.stringify({ 'Error in actionBasedOnChatResponse': err }));
  }
};
